import React, { useState, useEffect, useCallback, useRef } from "react";
import { useMutation, useQuery, useLazyQuery } from "@apollo/client";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Button } from "../ui/button";
import { Textarea } from "../ui/textarea";
import { Badge } from "../ui/badge";
import { ChevronDown, ChevronUp, AlertTriangle, Clock } from "lucide-react";
import {
  UPDATE_EVENT_MUTATION,
  GET_EVENT_TYPES,
  GET_ALL_EVENTS,
  GET_SHIFT_TIME_FOR_EMPLOYEE,
  GET_SHIFTS,
} from "../../graphql/queries";

interface Event {
  id: string;
  timestamp: string;
  note?: string;
  shiftId?: string;
  snapshotShiftName?: string;
  expectedShiftStart?: string;
  expectedShiftEnd?: string;
  employee: {
    id: string;
    firstName: string;
    lastName: string;
    department?: string;
  };
  eventType: {
    id: string;
    name: string;
    code: string;
    color: string;
  };
}

interface ShiftTimeResult {
  shiftId: string;
  shiftName: string;
  startTime: string;
  endTime: string;
  daysOfWeek: string[];
  shiftIndex: number;
  lateArrivalToleranceMinutes: number;
  earlyDepartureToleranceMinutes: number;
  earlyArrivalToleranceMinutes: number;
  lateDepartureToleranceMinutes: number;
}

interface ShiftData {
  id: string;
  name: string;
  isActive: boolean;
  shiftTimes: {
    id: string;
    startTime: string;
    endTime: string;
    daysOfWeek: string[];
    order: number;
  }[];
}

type SnapshotMode = "keep" | "recalculate" | "manual";

interface EditEventDialogProps {
  event: Event | null;
  open: boolean;
  onClose: () => void;
}

export const EditEventDialog: React.FC<EditEventDialogProps> = ({
  event,
  open,
  onClose,
}) => {
  const [timestamp, setTimestamp] = useState("");
  const [note, setNote] = useState("");
  const [eventTypeId, setEventTypeId] = useState("");

  // Shift snapshot state
  const [snapshotMode, setSnapshotMode] = useState<SnapshotMode>("keep");
  const [shiftSectionOpen, setShiftSectionOpen] = useState(false);
  const [shiftMismatch, setShiftMismatch] = useState(false);
  const [calculatedShift, setCalculatedShift] = useState<ShiftTimeResult | null>(null);

  // Manual override fields
  const [manualShiftId, setManualShiftId] = useState("");
  const [manualShiftName, setManualShiftName] = useState("");
  const [manualShiftStart, setManualShiftStart] = useState("");
  const [manualShiftEnd, setManualShiftEnd] = useState("");

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const initialTimestampRef = useRef<string>("");

  const { data: eventTypesData } = useQuery(GET_EVENT_TYPES);
  const eventTypes = eventTypesData?.eventTypes || [];

  const { data: shiftsData } = useQuery(GET_SHIFTS, {
    fetchPolicy: "cache-first",
  });
  const shifts: ShiftData[] = shiftsData?.shifts?.filter((s: ShiftData) => s.isActive) || [];

  const [fetchShiftTime] = useLazyQuery(GET_SHIFT_TIME_FOR_EMPLOYEE, {
    fetchPolicy: "network-only",
  });

  const [updateEvent, { loading: updating }] = useMutation(UPDATE_EVENT_MUTATION, {
    refetchQueries: [{ query: GET_ALL_EVENTS }],
    onCompleted: () => {
      onClose();
    },
    onError: (error) => {
      console.error("Error updating event:", error);
    },
  });

  // Reset state when event changes
  useEffect(() => {
    if (event) {
      const eventDate = new Date(event.timestamp);
      const formattedTimestamp = new Date(
        eventDate.getTime() - eventDate.getTimezoneOffset() * 60000
      )
        .toISOString()
        .slice(0, 16);

      setTimestamp(formattedTimestamp);
      initialTimestampRef.current = formattedTimestamp;
      setNote(event.note || "");
      setEventTypeId(event.eventType.id);
      setSnapshotMode("keep");
      setShiftSectionOpen(false);
      setShiftMismatch(false);
      setCalculatedShift(null);
      setManualShiftId(event.shiftId || "");
      setManualShiftName(event.snapshotShiftName || "");
      setManualShiftStart(event.expectedShiftStart || "");
      setManualShiftEnd(event.expectedShiftEnd || "");
    }
  }, [event]);

  // Check shift mismatch when timestamp changes
  const checkShiftMismatch = useCallback(
    async (newTimestamp: string) => {
      if (!event || event.eventType.code !== "WORK_START") return;

      try {
        const { data } = await fetchShiftTime({
          variables: {
            employeeId: event.employee.id,
            timestamp: new Date(newTimestamp).toISOString(),
          },
        });

        const result = data?.shiftTimeForEmployee as ShiftTimeResult | null;
        setCalculatedShift(result);

        // Compare with current snapshot
        const currentStart = event.expectedShiftStart;
        const currentEnd = event.expectedShiftEnd;
        const newStart = result?.startTime;
        const newEnd = result?.endTime;

        const hasMismatch =
          (currentStart !== newStart || currentEnd !== newEnd) &&
          (newStart !== undefined || currentStart !== undefined);

        setShiftMismatch(hasMismatch);

        // If no mismatch, reset to keep
        if (!hasMismatch && snapshotMode !== "manual") {
          setSnapshotMode("keep");
        }
      } catch (err) {
        console.error("Error fetching shift time:", err);
      }
    },
    [event, fetchShiftTime, snapshotMode]
  );

  // Debounced timestamp change handler
  const handleTimestampChange = useCallback(
    (value: string) => {
      setTimestamp(value);

      // Only check mismatch if timestamp actually changed from initial value
      if (value !== initialTimestampRef.current) {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
          checkShiftMismatch(value);
        }, 500);
      } else {
        setShiftMismatch(false);
        setCalculatedShift(null);
        if (snapshotMode !== "manual") {
          setSnapshotMode("keep");
        }
      }
    },
    [checkShiftMismatch, snapshotMode]
  );

  // Clean up debounce on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, []);

  // When user selects a shift from dropdown in manual mode
  const handleManualShiftSelect = (shiftId: string) => {
    setManualShiftId(shiftId);
    const selectedShift = shifts.find((s) => s.id === shiftId);
    if (selectedShift) {
      setManualShiftName(selectedShift.name);
      // Auto-fill first shift time slot if available
      if (selectedShift.shiftTimes.length === 1) {
        setManualShiftStart(selectedShift.shiftTimes[0].startTime);
        setManualShiftEnd(selectedShift.shiftTimes[0].endTime);
      }
    }
  };

  // When user selects a specific time slot for a shift with multiple slots
  const handleSlotSelect = (startTime: string, endTime: string) => {
    setManualShiftStart(startTime);
    setManualShiftEnd(endTime);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!event || !timestamp) {
      return;
    }

    try {
      const variables: any = {
        id: event.id,
        timestamp: new Date(timestamp).toISOString(),
        note: note || null,
        eventTypeId: eventTypeId || null,
      };

      if (snapshotMode === "recalculate") {
        variables.recalculateSnapshot = true;
      } else if (snapshotMode === "manual") {
        variables.shiftId = manualShiftId || null;
        variables.snapshotShiftName = manualShiftName || null;
        variables.expectedShiftStart = manualShiftStart || null;
        variables.expectedShiftEnd = manualShiftEnd || null;
      }
      // mode === "keep" — no snapshot variables sent

      await updateEvent({ variables });
    } catch (error) {
      console.error("Error updating event:", error);
    }
  };

  if (!event) return null;

  const selectedManualShift = shifts.find((s) => s.id === manualShiftId);
  const hasMultipleSlots =
    selectedManualShift && selectedManualShift.shiftTimes.length > 1;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[560px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edytuj wydarzenie</DialogTitle>
          <DialogDescription>
            Edytuj szczeg&oacute;ły wydarzenia dla pracownika{" "}
            {event.employee.lastName} {event.employee.firstName}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="timestamp">Data i godzina</Label>
            <Input
              id="timestamp"
              type="datetime-local"
              value={timestamp}
              onChange={(e) => handleTimestampChange(e.target.value)}
              required
            />
          </div>

          {/* Shift mismatch warning - only for WORK_START events */}
          {event.eventType.code === "WORK_START" && shiftMismatch && (
            <div className="rounded-md border border-yellow-300 bg-yellow-50 dark:border-yellow-700 dark:bg-yellow-950/30 p-3 space-y-2">
              <div className="flex items-start gap-2 text-sm">
                <AlertTriangle className="h-4 w-4 text-yellow-600 dark:text-yellow-500 mt-0.5 flex-shrink-0" />
                <div className="space-y-1">
                  <p className="font-medium text-yellow-800 dark:text-yellow-300">
                    Zmiana godziny mo&#380;e wymaga&#263; korekty zmiany
                  </p>
                  <p className="text-yellow-700 dark:text-yellow-400">
                    Obecna:{" "}
                    <span className="font-mono">
                      {event.expectedShiftStart || "?"}&ndash;
                      {event.expectedShiftEnd || "?"}
                    </span>
                    {event.snapshotShiftName && (
                      <span> ({event.snapshotShiftName})</span>
                    )}
                  </p>
                  {calculatedShift && (
                    <p className="text-yellow-700 dark:text-yellow-400">
                      Sugerowana:{" "}
                      <span className="font-mono">
                        {calculatedShift.startTime}&ndash;
                        {calculatedShift.endTime}
                      </span>{" "}
                      ({calculatedShift.shiftName})
                    </p>
                  )}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 pt-1">
                <Button
                  type="button"
                  variant={snapshotMode === "keep" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setSnapshotMode("keep");
                    setShiftSectionOpen(false);
                  }}
                >
                  Zachowaj obecn&#261;
                </Button>
                <Button
                  type="button"
                  variant={
                    snapshotMode === "recalculate" ? "default" : "outline"
                  }
                  size="sm"
                  onClick={() => {
                    setSnapshotMode("recalculate");
                    setShiftSectionOpen(false);
                  }}
                >
                  Przelicz automatycznie
                </Button>
                <Button
                  type="button"
                  variant={snapshotMode === "manual" ? "default" : "outline"}
                  size="sm"
                  onClick={() => {
                    setSnapshotMode("manual");
                    setShiftSectionOpen(true);
                  }}
                >
                  Edytuj r&#281;cznie
                </Button>
              </div>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="eventType">Typ wydarzenia</Label>
            <select
              id="eventType"
              className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50"
              value={eventTypeId}
              onChange={(e) => setEventTypeId(e.target.value)}
            >
              <option value="">Wybierz typ wydarzenia</option>
              {eventTypes.map((type: any) => (
                <option key={type.id} value={type.id}>
                  {type.name}
                </option>
              ))}
            </select>
          </div>

          <div className="space-y-2">
            <Label htmlFor="note">Notatka</Label>
            <Textarea
              id="note"
              placeholder="Dodaj notatk&#281; (opcjonalnie)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={3}
            />
          </div>

          {/* Collapsible shift section - only for WORK_START events */}
          {event.eventType.code === "WORK_START" && <div className="border rounded-md">
            <button
              type="button"
              className="flex w-full items-center justify-between p-3 hover:bg-accent/50 rounded-md transition-colors"
              onClick={() => setShiftSectionOpen(!shiftSectionOpen)}
            >
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <span className="text-sm font-medium">Zmiana</span>
                {(event.expectedShiftStart || event.expectedShiftEnd) && (
                  <Badge variant="outline" className="text-xs">
                    {event.snapshotShiftName || "?"}{" "}
                    {event.expectedShiftStart}&ndash;{event.expectedShiftEnd}
                  </Badge>
                )}
                {snapshotMode === "recalculate" && (
                  <Badge className="text-xs bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                    Przeliczenie
                  </Badge>
                )}
                {snapshotMode === "manual" && (
                  <Badge className="text-xs bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-200">
                    R&#281;czna edycja
                  </Badge>
                )}
              </div>
              {shiftSectionOpen ? (
                <ChevronUp className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              )}
            </button>

            {shiftSectionOpen && (
              <div className="px-3 pb-3 space-y-3 border-t pt-3">
                {/* Shift dropdown */}
                <div className="space-y-1">
                  <Label htmlFor="shiftSelect" className="text-xs">
                    Zmiana
                  </Label>
                  <select
                    id="shiftSelect"
                    className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                    value={manualShiftId}
                    onChange={(e) => {
                      handleManualShiftSelect(e.target.value);
                      if (snapshotMode !== "manual") {
                        setSnapshotMode("manual");
                      }
                    }}
                  >
                    <option value="">Brak zmiany</option>
                    {shifts.map((shift) => (
                      <option key={shift.id} value={shift.id}>
                        {shift.name}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Shift time slot buttons (if shift has multiple slots) */}
                {hasMultipleSlots && (
                  <div className="space-y-1">
                    <Label className="text-xs">
                      Przedzia&#322; czasowy
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {selectedManualShift.shiftTimes
                        .slice()
                        .sort((a, b) => a.order - b.order)
                        .map((st) => {
                          const isSelected =
                            manualShiftStart === st.startTime &&
                            manualShiftEnd === st.endTime;
                          return (
                            <Button
                              key={st.id}
                              type="button"
                              variant={isSelected ? "default" : "outline"}
                              size="sm"
                              onClick={() => {
                                handleSlotSelect(st.startTime, st.endTime);
                                if (snapshotMode !== "manual") {
                                  setSnapshotMode("manual");
                                }
                              }}
                            >
                              {st.startTime}&ndash;{st.endTime}
                            </Button>
                          );
                        })}
                    </div>
                  </div>
                )}

                {/* Manual time inputs */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="shiftStart" className="text-xs">
                      Pocz&#261;tek zmiany
                    </Label>
                    <Input
                      id="shiftStart"
                      type="time"
                      value={manualShiftStart}
                      onChange={(e) => {
                        setManualShiftStart(e.target.value);
                        if (snapshotMode !== "manual") {
                          setSnapshotMode("manual");
                        }
                      }}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label htmlFor="shiftEnd" className="text-xs">
                      Koniec zmiany
                    </Label>
                    <Input
                      id="shiftEnd"
                      type="time"
                      value={manualShiftEnd}
                      onChange={(e) => {
                        setManualShiftEnd(e.target.value);
                        if (snapshotMode !== "manual") {
                          setSnapshotMode("manual");
                        }
                      }}
                    />
                  </div>
                </div>
              </div>
            )}
          </div>}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              Anuluj
            </Button>
            <Button type="submit" disabled={updating}>
              {updating ? "Zapisywanie..." : "Zapisz zmiany"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
